import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SvMessageService } from './sv-message.service';
import { SvMessageType } from '../components/sv-message/sv-message.component';
import { PT_FALLBACK } from '../../i18n/pt-fallback';

type Key = string;

@Injectable({ providedIn: 'root' })
export class AlertMessageService {
  constructor(
    private t: TranslateService,
    private svMessage: SvMessageService,
  ) {}

  /** atalhos principais */
  error(messageKey: Key = 'ERRORS.REQUEST_FAILED', titleKey: Key = 'COMMON.ALERTS.ATTENTION', _opts: any = {}) {
    return this.svMessage.fire({
      type: 'error',
      title: this.tr(titleKey),
      text: this.tr(messageKey),
      confirmButtonText: 'OK',
    });
  }

  errorHtml(htmlContent: string, titleKey: Key = 'COMMON.ALERTS.ATTENTION') {
    return this.svMessage.fire({
      type: 'error',
      title: this.tr(titleKey),
      html: htmlContent,
      confirmButtonText: 'OK',
    });
  }

  success(messageKey: Key, _titleKey: Key = 'COMMON.ALERTS.SUCCESS', opts: any = {}) {
    return this.svMessage.success(this.tr(messageKey), this.tr(_titleKey), opts.timer || 3000);
  }

  saved(messageKey: Key = 'COMMON.ALERTS.SAVE_SUCCESS', titleKey: Key = 'COMMON.ALERTS.SUCCESS', opts: any = {}) {
    return this.svMessage.success(this.tr(messageKey), this.tr(titleKey), opts.timer || 3000);
  }
  warning(messageKey: Key, titleKey: Key = 'COMMON.ALERTS.ATTENTION', _opts: any = {}) {
    return this.svMessage.fire({
      type: 'warning',
      title: this.tr(titleKey),
      text: this.tr(messageKey),
      confirmButtonText: 'OK',
    });
  }
  info(messageKey: Key, _titleKey: Key = 'COMMON.ALERTS.INFO', _opts: any = {}) {
    return this.svMessage.info(messageKey, this.tr(_titleKey));
  }

  /** confirm simples (OK/Cancelar) -> true/false */
  async confirm(titleKey: Key = 'COMMON.ALERTS.CONFIRM', messageKey?: Key, opts: any = {}): Promise<boolean> {
    const result = await this.svMessage.fire({
      type: (opts.icon as SvMessageType) || 'question',
      title: this.tr(titleKey),
      text: messageKey ? this.tr(messageKey) : undefined,
      showCancelButton: true,
      confirmButtonText: opts.confirmButtonText || this.tr('ACTIONS.YES'),
      cancelButtonText: opts.cancelButtonText || this.tr('ACTIONS.NO'),
      reverseButtons: true,
    });
    return result.isConfirmed;
  }

  /** confirm delete "padrão" */
  confirmDelete(messageKey: Key = 'ALERTS.CONFIRM_DELETE', opts: any = {}) {
    return this.confirm('ALERTS.CONFIRM', messageKey, {
      icon: 'warning',
      confirmButtonText: this.tr('ACTIONS.DELETE'),
      cancelButtonText: this.tr('ACTIONS.CANCEL') || this.tr('ACTIONS.NO'),
      ...opts,
    });
  }

  /** mapear erros HTTP comuns para mensagens traduzidas */
  fromHttpError(status?: number, fallbackKey: Key = 'ERRORS.UNEXPECTED') {
    switch (status) {
      case 0:
        return this.error('ERRORS.OFFLINE');
      case 400:
        return this.error('ERRORS.BAD_REQUEST');
      case 401:
        return this.error('ERRORS.UNAUTHORIZED');
      case 403:
        return this.error('ERRORS.FORBIDDEN');
      case 404:
        return this.error('ERRORS.NOT_FOUND');
      case 408:
        return this.error('ERRORS.TIMEOUT');
      case 413:
        return this.error('ERRORS.PAYLOAD_TOO_LARGE');
      case 503:
        return this.error('ERRORS.UNAVAILABLE');
      default:
        return this.error(fallbackKey);
    }
  }

  /**
   * Diálogo rápido usando sv-message nativo (em substituição a toasts)
   */
  toast(messageKey: Key, icon: string = 'error', _opts: any = {}) {
    const type = icon as SvMessageType;
    const timer = _opts.timer || 3000;

    if (type === 'success') {
      return this.svMessage.success(messageKey, undefined, timer);
    }

    return this.svMessage.fire({
      type,
      title: type === 'error' ? 'Erro' : type === 'warning' ? 'Aviso' : 'Informação',
      text: this.tr(messageKey),
      timer: type === 'info' ? timer : undefined,
      showConfirmButton: type !== 'info',
      confirmButtonText: 'OK',
    });
  }

  /**
   * Traduz uma chave. Se não for chave válida, devolve o texto tal como está.
   * Quando os ficheiros JSON não carregam, usa fallback PT-PT hardcoded.
   */
  private tr(key: Key, params?: Record<string, any>) {
    if (!key) return '';
    const result = this.t.instant(key, params);
    // ngx-translate devolve [chave] quando não encontra a tradução
    if (result === `[${key}]`) return PT_FALLBACK[key] || key;
    // Se translate.instant() devolveu algo diferente da chave, a tradução existe
    if (result !== key) return result;
    // Fallback PT-PT antes de devolver a chave em bruto
    return PT_FALLBACK[key] || key;
  }
}
